import {
  DeleteOutlined,
  EditOutlined,
  PlusOutlined,
  SaveOutlined,
} from "@ant-design/icons";
import {
  Button,
  Card,
  Col,
  Form,
  Input,
  List,
  message,
  Modal,
  Row,
  Spin,
  Tag,
} from "antd";
import { useEffect, useState } from "react";
import { getSupaClient } from "../../lib/supabase";

/**
 * A component that displays the slack member directory, which consists of a card for editing a list of skills
 * and a card for editing a list of experience options. to be used in the settings page.
 * @returns JSX
 */
export default function () {
  const teamId = localStorage.getItem("team-id");
  const [loading, setLoading] = useState(false);
  const [selectedSkill, setSelectedSkill] = useState<any>({});
  const [isEditSkillModalVisible, setIsEditSkillModalVisible] = useState(false);
  const [skills, setSkills] = useState<any[]>([]);
  const [isAddSkillModalVisible, setIsAddSkillModalVisible] = useState(false);
  const [newSkill, setNewSkill] = useState({ name: "" });

  const [selectedExperience, setSelectedExperience] = useState<any>({});
  // eslint-disable-next-line prettier/prettier
  const [isEditExperienceModalVisible, setIsEditExperienceModalVisible] =
    useState(false);
  const [experiences, setExperiences] = useState<any[]>([]);
  // eslint-disable-next-line prettier/prettier
  const [isAddExperienceModalVisible, setIsAddExperienceModalVisible] =
    useState(false);
  const [newExperience, setNewExperience] = useState({ name: "" });
  const supaClient = getSupaClient();

  // loadSkills: This function is being used to load skills from supaClient

  const loadSkills = async () => {
    const { data, error } = await supaClient
      .from("directory_skills")
      .select("id, name")
      .eq("team", teamId);
    if (data) setSkills(data);
    if (error) {
      console.error("Error in loadSkills", error);
    }
  };

  // loadExperiences: This function is being used to load experiences from supaClient

  const loadExperiences = async () => {
    const { data, error } = await supaClient
      .from("directory_industry_experience")
      .select("id, name")
      .eq("team", teamId);
    if (data) setExperiences(data);
    if (error) {
      console.error("Error in loadExperiences", error);
    }
  };

  useEffect(() => {
    loadSkills();
    loadExperiences();
  }, []);

  // updateSkill: This function is being used to update skills in directory_skills table

  const updateSkill = async () => {
    setLoading(true);
    const { data, error } = await supaClient
      .from("directory_skills")
      .update(selectedSkill)
      .match({ id: selectedSkill.id });
    if (error) {
      message.error("Error editing tag. Please try again");
    }
    if (data) {
      setIsEditSkillModalVisible(false);
      await loadSkills();
      message.success("Edited successfully");
    }
    setLoading(false);
  };

  // deleteSkill: This function is being used to delete skills from directory_skills table

  const deleteSkill = async (item: any = selectedSkill) => {
    setLoading(true);
    if (item) {
      const { data, error } = await supaClient
        .from("directory_skills")
        .delete()
        .match({ id: item.id });
      if (error) {
        setLoading(false);
        message.error(
          "Error deleting tag - please check if there are members assigned to this skill before deleting. Or Please try again"
        );
      }
      if (data) {
        setIsEditSkillModalVisible(false);
        await loadSkills();
        setLoading(false);
        message.success("Deleted successfully");
      }
    }
  };

  const handleAddSkill = async () => {
    setLoading(true);
    const { data, error } = await supaClient.from("directory_skills").insert({
      name: newSkill.name,
      team: teamId,
    });
    if (error) message.error("Error adding skill. Please try again");
    if (data) {
      setIsAddSkillModalVisible(false);
      await loadSkills();
      message.success("Added successfully");
    }
    setLoading(false);
  };

  /**
   * This function is being used to update experiences in directory_industry_experience table
   */
  async function updateIndustryExperience() {
    setLoading(true);
    const { data, error } = await supaClient
      .from("directory_industry_experience")
      .update(selectedExperience)
      .match({ id: selectedExperience.id });
    if (error) {
      message.error("Error editing experience. Please try again");
    }
    if (data) {
      setIsEditExperienceModalVisible(false);
      await loadExperiences();
      message.success("Edited successfully");
    }
    setLoading(false);
  }

  // deleteIndustryExperience: This function is being used to delete experiences from directory_industry_experience table

  const deleteIndustryExperience = async (item: any = selectedExperience) => {
    setLoading(true);
    if (item) {
      const { data, error } = await supaClient
        .from("directory_industry_experience")
        .delete()
        .match({ id: item.id });
      if (error) {
        setLoading(false);
        message.error(
          "Error deleting experience - please check if there are members assigned to this experience before deleting. Or Please try again"
        );
      }
      if (data) {
        setIsEditExperienceModalVisible(false);
        await loadExperiences();
        setLoading(false);
        message.success("Deleted successfully");
      }
    }
  };

  // handleAddIndustryExperience: This function is being used to insert experiences in directory_industry_experience table

  const handleAddIndustryExperience = async () => {
    setLoading(true);
    const { data, error } = await supaClient
      .from("directory_industry_experience")
      .insert({
        name: newExperience.name,
        team: teamId,
      });
    if (error) {
      message.error("Error adding experience. Please try again");
    }
    if (data) {
      setIsAddExperienceModalVisible(false);
      await loadExperiences();
      message.success("Added successfully");
    }
    setLoading(false);
  };

  return (
    <div>
      <Spin spinning={loading}>
        <Row gutter={[32, 32]}>
          <Col span={12}>
            <Card
              title={
                <div className="flex justify-between mt-2">
                  <span>Edit Skill Options</span>
                  <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={() => {
                      window.analytics.track(
                        "Clicked add skill button in slack member directory",
                        {
                          platform: "slack",
                        }
                      );
                      setIsAddSkillModalVisible(true);
                    }}
                  >
                    Add
                  </Button>
                </div>
              }
            >
              <List
                size="large"
                className="skills-options"
                dataSource={skills}
                renderItem={(item) => (
                  <List.Item
                    actions={[
                      <Button
                        type="primary"
                        icon={<EditOutlined />}
                        size="small"
                        key={item.id}
                        onClick={() => {
                          window.analytics.track(
                            "Clicked edit skill button in member directory",
                            { item, platform: "slack" }
                          );
                          setSelectedSkill(item);
                          setIsEditSkillModalVisible(true);
                        }}
                      />,
                      <Button
                        type="dashed"
                        danger
                        size="small"
                        icon={<DeleteOutlined />}
                        key={item.id}
                        onClick={() => {
                          window.analytics.track(
                            "Clicked delete skill in member directory",
                            {
                              platform: "slack",
                              item,
                            }
                          );
                          setSelectedSkill(item);
                          deleteSkill(item);
                        }}
                      />,
                    ]}
                  >
                    <List.Item.Meta
                      title={
                        <Tag
                          color={item.color}
                          style={{ margin: 0 }}
                          key={item.id}
                        >
                          {item.name}
                        </Tag>
                      }
                    />
                  </List.Item>
                )}
              />
            </Card>
          </Col>
          <Col span={12}>
            <Card
              title={
                <div className="flex justify-between mt-2">
                  <span>Edit Experience Options</span>
                  <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={() => {
                      window.analytics.track(
                        "Clicked add experience button in slack member directory",
                        {
                          platform: "slack",
                        }
                      );
                      setIsAddExperienceModalVisible(true);
                    }}
                  >
                    Add
                  </Button>
                </div>
              }
            >
              <List
                size="large"
                className="skills-options"
                dataSource={experiences}
                renderItem={(item) => (
                  <List.Item
                    actions={[
                      <Button
                        type="primary"
                        icon={<EditOutlined />}
                        size="small"
                        key={item.id}
                        onClick={() => {
                          window.analytics.track(
                            "Clicked edit experience button in member directory",
                            { item, platform: "slack" }
                          );
                          setSelectedExperience(item);
                          setIsEditExperienceModalVisible(true);
                        }}
                      />,
                      <Button
                        type="dashed"
                        danger
                        size="small"
                        icon={<DeleteOutlined />}
                        key={item.id}
                        onClick={() => {
                          window.analytics.track(
                            "Clicked delete experience in member directory",
                            {
                              platform: "slack",
                              item,
                            }
                          );
                          setSelectedExperience(item);
                          deleteIndustryExperience(item);
                        }}
                      />,
                    ]}
                  >
                    <List.Item.Meta
                      title={
                        <Tag
                          color={item.color}
                          style={{ margin: 0 }}
                          key={item.id}
                        >
                          {item.name}
                        </Tag>
                      }
                    />
                  </List.Item>
                )}
              />
            </Card>
          </Col>
        </Row>

        <Modal
          title="Add Experience"
          onCancel={() => setIsAddExperienceModalVisible(false)}
          visible={isAddExperienceModalVisible}
          footer={null}
        >
          <div className="space-y-4">
            <div>
              Name:{" "}
              <Input
                value={newExperience.name}
                onChange={(e) =>
                  setNewExperience({
                    name: e.target.value,
                  })
                }
              />
            </div>
            <div className="flex flex-row justify-evenly">
              <Button
                type="primary"
                onClick={() => handleAddIndustryExperience()}
              >
                Save
              </Button>
            </div>
          </div>
        </Modal>
        <Modal
          title="Add Skill"
          onCancel={() => setIsAddSkillModalVisible(false)}
          visible={isAddSkillModalVisible}
          footer={null}
        >
          <div className="space-y-4">
            <div>
              Name:{" "}
              <Input
                value={newSkill.name}
                onChange={(e) =>
                  setNewSkill({
                    name: e.target.value,
                  })
                }
              />
            </div>
            <div className="flex flex-row justify-evenly">
              <Button type="primary" onClick={() => handleAddSkill()}>
                Save
              </Button>
            </div>
          </div>
        </Modal>
        <Modal
          title={`Edit Skill: ${selectedSkill.name}`}
          onCancel={() => setIsEditSkillModalVisible(false)}
          visible={isEditSkillModalVisible}
          footer={null}
        >
          <div className="space-y-4">
            <div>
              Name:{" "}
              <Input
                value={selectedSkill.name}
                onChange={(e) =>
                  setSelectedSkill({
                    ...selectedSkill,
                    name: e.target.value,
                  })
                }
              />
            </div>
            <div className="flex flex-row justify-evenly">
              <Button
                type="dashed"
                onClick={() => deleteSkill()}
                danger
                icon={<DeleteOutlined />}
              >
                Delete
              </Button>
              <Form.Item>
                <Button
                  type="primary"
                  onClick={() => updateSkill()}
                  icon={<SaveOutlined />}
                >
                  Update
                </Button>
              </Form.Item>
            </div>
          </div>
        </Modal>
        <Modal
          title={`Edit Experience: ${selectedExperience.name}`}
          onCancel={() => setIsEditExperienceModalVisible(false)}
          visible={isEditExperienceModalVisible}
          footer={null}
        >
          <div className="space-y-4">
            <div>
              Name:{" "}
              <Input
                value={selectedExperience.name}
                onChange={(e) =>
                  setSelectedExperience({
                    ...selectedExperience,
                    name: e.target.value,
                  })
                }
              />
            </div>
            <div className="flex flex-row justify-evenly">
              <Button
                type="dashed"
                onClick={() => deleteIndustryExperience()}
                danger
                icon={<DeleteOutlined />}
              >
                Delete
              </Button>
              <Form.Item>
                <Button
                  type="primary"
                  onClick={() => updateIndustryExperience()}
                  icon={<SaveOutlined />}
                >
                  Update
                </Button>
              </Form.Item>
            </div>
          </div>
        </Modal>
      </Spin>
    </div>
  );
}
